import { ChangeEvent, Ref, useCallback, useEffect, useMemo, useRef } from 'react';
import { Theme, css } from '@emotion/react';
import { Input, InputProps } from 'client/components/Input';

const inputPostfixStyle = (theme: Theme) => css`
  position: absolute;
  font-size: 1em;
  font-family: ${theme.font.body};
  letter-spacing: normal;
  color: ${theme.color.foreground};
  margin-left: 12px;
  bottom: 8px;
  pointer-events: none;
  user-select: none;
  opacity: 0.5;
`;

const inputMeasurerStyle = (theme: Theme) => css`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  font-size: 1em;
  font-family: ${theme.font.body};
  letter-spacing: normal;
  white-space: pre;
`;

export interface PostfixedInputProps extends InputProps {
  postfix: string;
}

export const PostfixedInput = ({ value, postfix, onChange, ref, ...other }: PostfixedInputProps) => {
  const postfixRef = useRef<null | HTMLDivElement>(null);
  const measurerRef = useRef<null | HTMLDivElement>(null);

  const updatePostfixPosition = (value: string) => {
    if (measurerRef.current) {
      const measurer = measurerRef.current;
      measurer.innerText = value;
      const width = measurer.clientWidth;
      postfixRef.current?.setAttribute('style', `left: ${width}px;`);
    }
  };

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      updatePostfixPosition(event.target.value);
      if (onChange) onChange(event);
    },
    [onChange]
  );

  useEffect(() => {
    document.fonts.ready.then(() => updatePostfixPosition(value || ''));
  }, []);

  useEffect(() => {
    updatePostfixPosition(value || '');
  }, [value]);

  const postfixComponent = useMemo(
    () => (
      <div ref={postfixRef} css={inputPostfixStyle} className="postfix">
        {postfix}
      </div>
    ),
    [postfix]
  );

  return (
    <>
      <Input
        value={value}
        endAdornment={postfixComponent}
        ref={ref as Ref<HTMLInputElement>}
        hideEditIcon
        onChange={handleChange}
        {...other}
      />
      <div z-tabIndex={-1} ref={measurerRef} css={inputMeasurerStyle}>
        {value}
      </div>
    </>
  );
};
